import { useEffect, useState } from "react";
import type { CreatedPost, GraffitiStroke, Post } from "./api";
import {
  claimGift,
  createGift,
  createGiftDraft,
  createMint,
  createPost,
  fetchWall,
  reportPost,
  uploadImage,
} from "./api";
import { haptic, mySlug, startParam, targetSlug, tg } from "./telegram";
import { Graffiti } from "./Graffiti";
import { GraffitiView } from "./GraffitiView";
import { Discover } from "./Discover";

const RARITY_RU: Record<string, string> = {
  common: "Обычная",
  rare: "Редкая",
  epic: "Эпическая",
  legendary: "Легендарная",
};

const GIFTS: { emoji: string; stars: number }[] = [
  { emoji: "🌹", stars: 1 },
  { emoji: "🎁", stars: 5 },
  { emoji: "🧸", stars: 15 },
  { emoji: "🍾", stars: 50 },
  { emoji: "💎", stars: 100 },
  { emoji: "🔥", stars: 250 },
];

export function App() {
  const [slug, setSlug] = useState<string>(() => targetSlug());
  const [title, setTitle] = useState<string | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [anon, setAnon] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [discover, setDiscover] = useState(false);
  const [gifting, setGifting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchWall(slug)
      .then((data) => {
        if (!active) return;
        setPosts(data.posts);
        setTitle(data.wall.title);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setPosts([]);
        setTitle(null);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug, reloadKey]);

  useEffect(() => {
    const sp = startParam();
    if (sp.startsWith("claim_")) {
      claimGift(sp.slice("claim_".length))
        .then((r) => {
          if (r.ok) {
            setError(null);
            setSlug(mySlug());
            setReloadKey((k) => k + 1);
          }
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : "Подарок не забрался");
        });
    }
  }, []);

  const heading = title ?? `Стена @${slug}`;

  function optimisticAdd(
    type: Post["type"],
    body: string | null,
    strokes?: GraffitiStroke[],
  ): number {
    const tempId = -Date.now();
    const temp: Post = {
      id: tempId,
      authorId: null,
      isAnonymous: anon,
      type,
      body,
      status: "pending",
      createdAt: new Date().toISOString(),
      graffiti: strokes ?? null,
    };
    setPosts((p) => [temp, ...p]);
    return tempId;
  }

  function reconcile(tempId: number, res: CreatedPost) {
    if (res.status === "hidden") {
      setPosts((p) => p.filter((x) => x.id !== tempId));
      setError("Запись скрыта фильтром модерации");
      return;
    }
    setPosts((p) =>
      p.map((x) =>
        x.id === tempId
          ? {
              ...x,
              id: res.postId,
              status: "visible",
              rarity: res.rarity ?? null,
              edition: res.edition ?? null,
              signature: res.signature ?? null,
            }
          : x,
      ),
    );
  }

  async function sendText() {
    const body = text.trim();
    if (!body) return;
    haptic();
    const tempId = optimisticAdd("text", body);
    setText("");
    try {
      reconcile(tempId, await createPost(slug, { type: "text", body, isAnonymous: anon }));
    } catch (e) {
      setPosts((p) => p.filter((x) => x.id !== tempId));
      setError(e instanceof Error ? e.message : "Не отправилось");
    }
  }

  async function sendGraffiti(dataUrl: string, asGift: boolean) {
    setDrawing(false);
    if (!dataUrl) return;
    haptic("medium");
    const tempId = -Date.now();
    const temp: Post = {
      id: tempId,
      authorId: null,
      isAnonymous: anon,
      type: "media",
      body: null,
      status: "pending",
      createdAt: new Date().toISOString(),
      mediaKey: dataUrl,
      graffiti: null,
    };
    setPosts((p) => [temp, ...p]);
    try {
      const { mediaKey } = await uploadImage(dataUrl);
      setPosts((p) => p.map((x) => (x.id === tempId ? { ...x, mediaKey } : x)));
      reconcile(
        tempId,
        await createPost(slug, { type: "media", mediaKey, isAnonymous: anon, asGift }),
      );
    } catch (e) {
      setPosts((p) => p.filter((x) => x.id !== tempId));
      setError(e instanceof Error ? e.message : "Не отправилось");
    }
  }

  async function sendGift(stars: number, emoji: string) {
    try {
      const { link } = await createGift(slug, stars, emoji);
      setGifting(false);
      if (tg?.openInvoice) {
        tg.openInvoice(link, (status) => {
          if (status === "paid") {
            haptic("medium");
            setReloadKey((k) => k + 1);
          }
        });
      } else {
        window.open(link, "_blank");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Подарок не отправился");
      setGifting(false);
    }
  }

  async function mint(id: number) {
    if (id < 0) return;
    try {
      const { link } = await createMint(id);
      if (tg?.openInvoice) {
        tg.openInvoice(link, (status) => {
          if (status === "paid") {
            haptic("medium");
            setReloadKey((k) => k + 1);
          }
        });
      } else {
        window.open(link, "_blank");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Минт не удался");
    }
  }

  async function sendToFriend(dataUrl: string) {
    setDrawing(false);
    if (!dataUrl) return;
    try {
      const { mediaKey } = await uploadImage(dataUrl);
      const { preparedMessageId } = await createGiftDraft(mediaKey);
      if (tg?.shareMessage) {
        tg.shareMessage(preparedMessageId, (sent) => {
          if (sent) haptic("medium");
        });
      } else {
        setError("Отправка другу доступна в новых версиях Telegram");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не получилось отправить другу");
    }
  }

  function handleSave(dataUrl: string, action: "post" | "gift" | "friend") {
    if (action === "friend") void sendToFriend(dataUrl);
    else void sendGraffiti(dataUrl, action === "gift");
  }

  async function report(id: number) {
    if (id < 0) return;
    try {
      const r = await reportPost(id);
      if (r.hidden) setPosts((p) => p.filter((x) => x.id !== id));
    } catch {
      /* ignore report errors */
    }
  }

  if (discover) {
    return (
      <Discover
        ownSlug={mySlug()}
        onClose={() => setDiscover(false)}
        onPick={(s) => {
          setSlug(s);
          setDiscover(false);
          setError(null);
          haptic();
        }}
      />
    );
  }

  if (drawing) {
    return (
      <Graffiti
        onSave={(dataUrl, action) => handleSave(dataUrl, action)}
        onCancel={() => setDrawing(false)}
      />
    );
  }

  if (gifting) {
    return (
      <div className="discover">
        <div className="discover-head">
          <button className="ghost" onClick={() => setGifting(false)}>
            ← Назад
          </button>
          <span className="discover-title">Подарок на стену 🎁</span>
        </div>
        <div className="gift-grid">
          {GIFTS.map((g) => (
            <button
              key={g.emoji}
              className="gift-opt"
              onClick={() => void sendGift(g.stars, g.emoji)}
            >
              <span className="gift-emoji">{g.emoji}</span>
              <span className="gift-stars">{g.stars} ⭐</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="wall-header">
        <button className="icon-btn" onClick={() => setDiscover(true)} aria-label="Куда заглянем">
          🧭
        </button>
        <div className="avatar">{heading.replace(/^Стена @/, "").slice(0, 1).toUpperCase()}</div>
        <div>
          <div className="wall-name">{heading}</div>
          <div className="wall-sub">оставь запись или нарисуй граффити 🎨</div>
        </div>
      </header>

      {error && <div className="banner error">{error}</div>}

      {loading ? (
        <div className="banner">Загрузка…</div>
      ) : posts.length === 0 ? (
        <div className="banner">Здесь пока пусто. Будь первым! ✍️</div>
      ) : (
        <ul className="feed">
          {posts.map((p) => (
            <li key={p.id} className={`post ${p.status}`}>
              <div className="post-meta">
                <span className="post-author">
                  {p.isAnonymous ? "аноним" : p.authorName ? `@${p.authorName}` : "запись"}
                  {!p.isAnonymous && p.authorVerified && (
                    <span className="verified" title={p.authorTitle ?? "verified"}>
                      ✓
                    </span>
                  )}
                </span>
                {p.type === "graffiti" && <span className="tag">граффити</span>}
                {p.status === "pending" && <span className="tag pending">проверяю…</span>}
                {p.id > 0 && (
                  <button
                    className="report-btn"
                    onClick={() => void report(p.id)}
                    aria-label="Пожаловаться"
                  >
                    ⚑
                  </button>
                )}
              </div>
              {p.body && <div className="post-body">{p.body}</div>}
              {p.type === "graffiti" &&
                (p.graffiti && p.graffiti.length > 0 ? (
                  <GraffitiView strokes={p.graffiti} />
                ) : (
                  <div className="post-graffiti">🎨</div>
                ))}
              {p.type === "gift" && <div className="post-gift">{p.body}</div>}
              {p.mediaKey && <img className="post-image" src={p.mediaKey} alt="граффити" loading="lazy" />}
              {p.type === "graffiti" && p.rarity && (
                <div className={`collectible ${p.rarity}${p.signature ? " signature" : ""}`}>
                  {p.signature ? "✦ Подпись · " : "✨ "}
                  {RARITY_RU[p.rarity] ?? p.rarity} · #{p.edition}
                </div>
              )}
              {p.type === "graffiti" && !p.rarity && p.id > 0 && (
                <button className="mint-btn" onClick={() => void mint(p.id)}>
                  ✨ Сделать коллекционным · 25⭐
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="composer">
        <button
          className="icon-btn"
          onClick={() => {
            haptic();
            setDrawing(true);
          }}
          aria-label="Граффити"
        >
          🎨
        </button>
        <button
          className="icon-btn"
          onClick={() => {
            haptic();
            setGifting(true);
          }}
          aria-label="Подарок"
        >
          🎁
        </button>
        <input
          className="composer-input"
          placeholder={`Написать на «${heading}»…`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void sendText();
          }}
        />
        <button className="send-btn" onClick={() => void sendText()} aria-label="Отправить">
          ➤
        </button>
        <label className="anon-toggle">
          <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} />
          аноним
        </label>
      </div>
    </div>
  );
}
